import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {
  type Cadence,
  type SchedulableAuditType,
  type BrandAxisAuditType,
  describeCadence,
  resolveCadence,
  isCadenceConfigurable,
  isBrandAxisAuditType,
} from '@traaker/shared';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { formatDate } from '../../i18n/format';

/**
 * The `PATCH /brands/:id/audit-config` body for one axis: the stored map (the endpoint
 * replaces it wholesale, so a partial send would wipe sibling axes) with our axis set,
 * minus every `brand_*` key: the prompt cadence is written through `promptCadence` only
 * (Prompt Studio G 6b) and the API refuses a `brand_*` key in `cadenceOverride`.
 */
export function buildAxisSchedulePatchBody(
  stored: Partial<Record<SchedulableAuditType, Cadence>> | undefined,
  axis: Exclude<SchedulableAuditType, BrandAxisAuditType>,
  cadence: Cadence,
): { cadenceOverride: Partial<Record<SchedulableAuditType, Cadence>> } {
  const merged: Partial<Record<SchedulableAuditType, Cadence>> = {};
  for (const [k, v] of Object.entries(stored ?? {}) as [SchedulableAuditType, Cadence][]) {
    if (!isBrandAxisAuditType(k)) merged[k] = v;
  }
  merged[axis] = cadence;
  return { cadenceOverride: merged };
}

interface Props {
  brandId: string;
  tier: string;
  axis: SchedulableAuditType;
  /** Full per-axis cadence map from the brand row — we merge our axis into it on save. */
  cadenceOverride: Partial<Record<SchedulableAuditType, Cadence>> | undefined;
  onSaved: () => Promise<void> | void;
}

type NextRun = { axis: SchedulableAuditType; nextRunAt: string | null };

// Day-level only: the cron fire has a FlexibleTimeWindow (±4h to ±72h by
// cadence), so showing an exact hour:minute would be misleading. A date is the
// honest granularity.
function formatNextRun(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return formatDate(d, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

type CadenceUnit = 'days' | 'weeks' | 'months';
const DEFAULT_NEW_CADENCE = { interval: 7, unit: 'days' as CadenceUnit };

/**
 * Inline, single-axis schedule control for an audit page header.
 *
 * Shows a chip with the active cadence + next run (edit / delete) when a
 * schedule exists for this brand×axis, or a "Programmer" button otherwise.
 * All mutations go through `PATCH /brands/:id/audit-config` — we read the
 * brand's FULL `cadenceOverride` map, change only our axis, and send the whole
 * map back (the endpoint replaces the map wholesale, so a partial send would
 * wipe sibling axes). website / earned_media cadence is configurable on every
 * tier since pricing v2 (`isCadenceConfigurable`); the control still self-guards.
 */
export default function AxisScheduleControl({
  brandId,
  tier,
  axis,
  cadenceOverride,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [nextRunAt, setNextRunAt] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [interval, setIntervalValue] = useState(DEFAULT_NEW_CADENCE.interval);
  const [unit, setUnit] = useState<CadenceUnit>('days');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Effective cadence for this axis (override wins, else tier default). For
  // agency the tier default is 'off', so this is effectively the override.
  const effective = resolveCadence(cadenceOverride, tier, axis);
  const isScheduled = effective !== 'off';

  useEffect(() => {
    if (isBrandAxisAuditType(axis)) return;
    let cancelled = false;
    api
      .get<NextRun[]>(`/brands/${brandId}/next-runs`)
      .then((rows) => {
        if (cancelled) return;
        const row = rows.find((r) => r.axis === axis);
        setNextRunAt(row?.nextRunAt ?? null);
      })
      .catch(() => {
        if (!cancelled) setNextRunAt(null);
      });
    return () => {
      cancelled = true;
    };
  }, [brandId, axis, cadenceOverride]);

  // The prompt cadence (`brand_*` axes) is set in the Prompt Studio footer only.
  if (isBrandAxisAuditType(axis)) return null;

  // A tier that can't change this axis' cadence runs on the tier default: show a
  // read-only pill with the next automatic audit date. Unreachable for
  // website / earned_media since pricing v2 (configurable on every tier).
  if (!isCadenceConfigurable(tier, axis)) {
    if (!isScheduled) return null;
    return (
      <Tooltip title={t('axisSchedule.autoTooltip')}>
        <Chip
          icon={<AutorenewIcon sx={{ fontSize: 16 }} />}
          variant="outlined"
          color="success"
          label={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {t('axisSchedule.nextAutoAudit')}
              <Typography component="span" variant="caption" fontWeight={700}>
                {formatNextRun(nextRunAt)}
              </Typography>
            </Box>
          }
          sx={{ '& .MuiChip-label': { px: 1 } }}
        />
      </Tooltip>
    );
  }

  const openEditor = (el: HTMLElement) => {
    // Seed the popover from the current cadence, or a sensible weekly default.
    const seed = effective !== 'off' && typeof effective === 'object' ? effective : DEFAULT_NEW_CADENCE;
    setIntervalValue(seed.interval);
    setUnit(seed.unit);
    setError(null);
    setAnchorEl(el);
  };

  // Merge our axis into the full map, then PATCH (the endpoint replaces the
  // whole cadenceOverride map). `'off'` removes the schedule.
  const persist = async (cadence: Cadence) => {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/brands/${brandId}/audit-config`, buildAxisSchedulePatchBody(cadenceOverride, axis, cadence));
      setAnchorEl(null);
      await onSaved();
    } catch (err: any) {
      setError(err?.message || t('axisSchedule.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => persist('off');
  const handleSaveEditor = () =>
    persist({ interval: Math.max(1, Math.min(30, interval)), unit });

  return (
    <>
      {isScheduled ? (
        <Chip
          icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
          variant="outlined"
          color="primary"
          label={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {describeCadence(effective)}
              <Typography component="span" variant="caption" color="text.secondary">
                {t('axisSchedule.nextRunInline', { date: formatNextRun(nextRunAt) })}
              </Typography>
            </Box>
          }
          onDelete={handleDelete}
          deleteIcon={
            <Tooltip title={t('axisSchedule.deleteSchedule')}>
              <CloseIcon />
            </Tooltip>
          }
          // The chip body opens the editor; the delete icon removes the schedule.
          onClick={(e) => openEditor(e.currentTarget as HTMLElement)}
          sx={{ cursor: 'pointer', '& .MuiChip-label': { px: 1 } }}
        />
      ) : (
        <Button
          variant="outlined"
          size="medium"
          startIcon={<ScheduleIcon />}
          onClick={(e) => openEditor(e.currentTarget)}
        >
          {t('axisSchedule.schedule')}
        </Button>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <EditIcon sx={{ fontSize: 16, color: 'action.active' }} />
              <Typography variant="subtitle2" fontWeight={700}>
                {t('axisSchedule.editorTitle')}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t('axisSchedule.editorHelp')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                type="number"
                label={t('axisSchedule.every')}
                value={interval}
                inputProps={{ min: 1, max: 30 }}
                onChange={(e) =>
                  setIntervalValue(Math.max(1, Math.min(30, parseInt(e.target.value, 10) || 1)))
                }
                sx={{ width: 100 }}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select value={unit} onChange={(e) => setUnit(e.target.value as any)}>
                  <MenuItem value="days">{t('axisSchedule.unitDays')}</MenuItem>
                  <MenuItem value="weeks">{t('axisSchedule.unitWeeks')}</MenuItem>
                  <MenuItem value="months">{t('axisSchedule.unitMonths')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {error && (
              <Typography color="error" variant="caption">
                {error}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {isScheduled ? (
                <Button size="small" color="error" disabled={saving} onClick={handleDelete}>
                  {t('axisSchedule.delete')}
                </Button>
              ) : (
                <span />
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" disabled={saving} onClick={() => setAnchorEl(null)}>
                  {t('axisSchedule.cancel')}
                </Button>
                <Button size="small" variant="contained" disabled={saving} onClick={handleSaveEditor}>
                  {saving ? t('axisSchedule.saving') : t('axisSchedule.save')}
                </Button>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
